import {
  Box,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Picture, TImage } from '@shared/ui'

import { ReactComponent as ArbiMedalIcon } from '../../assets/arbimedal.svg'
import { ReactComponent as DevelopmentIcon } from '../../assets/development.svg'
import { ReactComponent as DigitalisationIcon } from '../../assets/digitalisation.svg'
import { ReactComponent as PartnerIcon } from '../../assets/partner.svg'
import { ReactComponent as ResearchIcon } from '../../assets/research.svg'
import arbiupLogosMobilePng from '../../assets/up-logos-mobile.png'
import arbiupLogosMobileWebp from '../../assets/up-logos-mobile.webp'
import arbiupLogosPng from '../../assets/up-logos.png'
import arbiupLogosWebp from '../../assets/up-logos.webp'

import { ProjectTitle, ProjectName } from '../components'

const BoardElement = styled('div')({})

const Board = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(7.5),
  marginTop: theme.spacing(12),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}))

const boadIconStyles = {
  xs: {
    minWidth: '64px',
    height: '64px',
  },
  md: {
    minWidth: '72px',
    height: '72px',
  },
}

export function StartapPlatformProject() {
  const { t } = useTranslation('widgets')

  const theme = useTheme()

  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <Stack>
      <ProjectTitle number={3} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-end"
        flex={1}
        gap={{
          xs: 4,
          md: 7.5,
        }}
      >
        <Stack gap={{ xs: 2.5, md: 5, flex: 1 }}>
          <ProjectName
            sx={{
              mt: { xs: 3, md: 4.5 },
            }}
            name={t('main-page.projects.arbiup.name')}
            description={t('main-page.projects.arbiup.description')}
          />

          <Stack gap={{ xs: 1.5, md: 2.5 }}>
            <Typography
              fontStyle="italic"
              variant={isMdAndUp ? 'text20Italic' : 'text16Regular'}
            >
              {t('main-page.projects.arbiup.text-1')}
            </Typography>

            <Typography
              sx={{ fontWeight: 400 }}
              variant={isMdAndUp ? 'text18Regular' : 'text16Regular'}
            >
              {t('main-page.projects.arbiup.text-2')}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          flex={1}
          sx={{
            background:
              'linear-gradient(0deg, #F3F5F9, #F3F5F9), linear-gradient(87.29deg, #E7ECF4 2.26%, #D0D8ED 52%, #CDDBFB 97.74%)',
            borderRadius: '20px',
            padding: '64px 0 32px 0',
            position: 'relative',
          }}
        >
          <ArbiMedalIcon
            style={{
              position: 'absolute',
              top: -38,
              right: 16,
              width: isMdAndUp ? 'auto' : '66px',
            }}
          />

          <Box
            paddingX={{ xs: 3, md: 4 }}
            display="flex"
            flexDirection="column"
          >
            <Typography variant="text16Regular">
              {t('main-page.projects.arbiup.banner.title')}
            </Typography>

            <Typography mt={{ md: 2.5 }} variant="text16Regular">
              {t('main-page.projects.arbiup.banner.amount')}
            </Typography>
          </Box>

          <Picture
            png={
              isMdAndUp
                ? (arbiupLogosPng as TImage<'png'>)
                : (arbiupLogosMobilePng as TImage<'png'>)
            }
            webp={
              isMdAndUp
                ? (arbiupLogosWebp as TImage<'webp'>)
                : (arbiupLogosMobileWebp as TImage<'webp'>)
            }
            alt="arbiup"
            sx={{
              flex: 1,
              maxHeight: { md: '370px' },
              mt: { xs: 2, md: 3 },
            }}
          />
        </Stack>
      </Stack>

      <Board>
        <Stack gap={5}>
          <BoardElement>
            <Stack direction="row" gap={2.5}>
              <DevelopmentIcon
                style={boadIconStyles[isMdAndUp ? 'md' : 'xs']}
              />

              <Stack>
                <Typography variant="text18Bold">
                  {t('main-page.projects.arbiup.board.elements.0.title')}
                </Typography>

                <Typography variant="text16Regular">
                  {t('main-page.projects.arbiup.board.elements.0.description')}
                </Typography>
              </Stack>
            </Stack>
          </BoardElement>

          <BoardElement>
            <Stack direction="row" gap={2.5}>
              <ResearchIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

              <Stack>
                <Typography variant="text18Bold">
                  {t('main-page.projects.arbiup.board.elements.1.title')}
                </Typography>

                <Typography variant="text16Regular">
                  {t('main-page.projects.arbiup.board.elements.1.description')}
                </Typography>
              </Stack>
            </Stack>
          </BoardElement>
        </Stack>

        <Stack gap={5}>
          <BoardElement>
            <Stack direction="row" gap={2.5}>
              <PartnerIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

              <Stack>
                <Typography variant="text18Bold">
                  {t('main-page.projects.arbiup.board.elements.2.title')}
                </Typography>

                <Typography variant="text16Regular">
                  {t('main-page.projects.arbiup.board.elements.2.description')}
                </Typography>
              </Stack>
            </Stack>
          </BoardElement>

          <BoardElement>
            <Stack direction="row" gap={2.5}>
              <DigitalisationIcon
                style={boadIconStyles[isMdAndUp ? 'md' : 'xs']}
              />

              <Stack>
                <Typography variant="text18Bold">
                  {t('main-page.projects.arbiup.board.elements.3.title')}
                </Typography>

                <Typography variant="text16Regular">
                  {t('main-page.projects.arbiup.board.elements.3.description')}
                </Typography>
              </Stack>
            </Stack>
          </BoardElement>
        </Stack>
      </Board>
    </Stack>
  )
}
